import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'
import { ColorPicker } from '../components/ui/ColorPicker'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

// Validates the token and returns invite metadata before account creation
async function fetchInviteDetails(token) {
  const { data, error } = await supabase.rpc('get_invite_by_token', { p_token: token })
  if (error || !data?.length) return null
  return data[0]
}

export const AcceptInvitePage = () => {
  const { token }  = useParams()
  const navigate   = useNavigate()

  const [invite,      setInvite]      = useState(null)   // { email, household_name, role, is_valid }
  const [checking,    setChecking]    = useState(true)
  const [checkError,  setCheckError]  = useState('')

  const [displayName, setDisplayName] = useState('')
  const [password,    setPassword]    = useState('')
  const [color,       setColor]       = useState('violet')
  const [errors,      setErrors]      = useState({})
  const [submitting,  setSubmitting]  = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Load invite details on mount
  useEffect(() => {
    if (!token) { setCheckError('Invalid invite link.'); setChecking(false); return }

    fetchInviteDetails(token).then((data) => {
      setChecking(false)
      if (!data) { setCheckError('This invite link is invalid or has expired.'); return }
      if (!data.is_valid) {
        setCheckError(data.used_at ? 'This invite has already been used.' : 'This invite link has expired.')
        return
      }
      setInvite(data)
    })
  }, [token])

  const validate = () => {
    const e = {}
    if (!displayName.trim()) e.displayName = 'Please enter your name.'
    if (password.length < 8) e.password = 'Password must be at least 8 characters.'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitError('')
    setSubmitting(true)

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/invite-user`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, display_name: displayName.trim(), password }),
      })

      const body = await res.json()

      if (!res.ok) {
        setSubmitError(body.error ?? 'Something went wrong. Please try again.')
        setSubmitting(false)
        return
      }

      // Update the member color now that we have a session
      if (body.access_token) {
        await supabase.auth.setSession({
          access_token:  body.access_token,
          refresh_token: body.refresh_token,
        })
        // Update profile color
        await supabase
          .from('profiles')
          .update({ display_name: displayName.trim(), member_color: color })
          .eq('id', body.user_id)
      }

      navigate('/calendar', { replace: true })
    } catch {
      setSubmitError('Network error. Please check your connection and try again.')
      setSubmitting(false)
    }
  }

  // ── Loading state ──────────────────────────────────────────────────────────
  if (checking) {
    return (
      <div className="min-h-full flex items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    )
  }

  // ── Invalid / expired invite ───────────────────────────────────────────────
  if (checkError) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center bg-background px-5 py-12 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-100 text-rose-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-text-primary mb-2">Invite invalid</h1>
        <p className="text-sm text-text-secondary max-w-xs">{checkError}</p>
        <p className="mt-4 text-xs text-text-muted">Ask your household admin for a new invite link.</p>
      </div>
    )
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-full flex flex-col items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-4 shadow-card">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">You're invited!</h1>
          <p className="text-sm text-text-muted mt-1">
            Join <strong className="text-text-primary">{invite.household_name}</strong> on DoorChart
          </p>
        </div>

        {/* Invite summary */}
        <div className="bg-primary-light rounded-base px-4 py-3 mb-6 flex items-center gap-3">
          <div className="text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <div className="text-sm">
            <span className="text-text-secondary">Invited as </span>
            <span className="font-medium text-primary capitalize">{invite.role}</span>
            <span className="text-text-muted"> • {invite.email}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Your name"
            type="text"
            placeholder="How should we call you?"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={errors.displayName}
            autoComplete="name"
          />

          <Input
            label="Choose a password"
            type="password"
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            autoComplete="new-password"
          />

          <ColorPicker
            label="Your member color"
            value={color}
            onChange={setColor}
          />

          {submitError && (
            <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
              {submitError}
            </div>
          )}

          <Button type="submit" className="w-full" size="lg" disabled={submitting}>
            {submitting ? <><Spinner size="sm" /> Creating account…</> : 'Join household'}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-text-muted">
          Already have an account?{' '}
          <a href="/login" className="text-primary hover:underline">Sign in</a>
        </p>
      </div>
    </div>
  )
}
